import React, { useState, useEffect } from 'react'
import { Package, Check, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import { Button } from '@/app/components/ui/button'
import { ProductListItem } from '@/app/services/product.service'
import { Spinner } from '@/app/lib/loader/loader'

interface BranchStockModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  product: ProductListItem | null
  onUpdateThresholds: (
    productId: number,
    sucursalId: number,
    min: number,
    max: number
  ) => Promise<void>
}

export function BranchStockModal({
  open,
  onOpenChange,
  product,
  onUpdateThresholds,
}: BranchStockModalProps) {
  const [edits, setEdits] = useState<Record<number, { min: number; max: number }>>({})
  const [savingId, setSavingId] = useState<number | null>(null)

  useEffect(() => {
    if (open && product?.stocks) {
      const initialEdits: Record<number, { min: number; max: number }> = {}
      product.stocks.forEach((s) => {
        initialEdits[s.sucursal_id] = { min: s.stock_min, max: s.stock_max }
      })
      setEdits(initialEdits)
    }
  }, [open, product])

  const handleSave = async (sucursalId: number) => {
    if (!product) return
    const current = edits[sucursalId]
    if (!current) return

    setSavingId(sucursalId)
    try {
      await onUpdateThresholds(product.id, sucursalId, current.min, current.max)
    } catch (e) {
      console.error(e)
    } finally {
      setSavingId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gestión de Stock por Sucursal</DialogTitle>
          <DialogDescription>
            Configura los límites mínimos y máximos de stock para{' '}
            <strong>{product?.name}</strong>.
          </DialogDescription>
        </DialogHeader>

        {!product?.stocks || product.stocks.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center text-slate-500">
            <Package className="mb-3 h-10 w-10 text-slate-300" />
            <p>No hay información de stock disponible para este producto.</p>
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Sucursal</th>
                  <th className="px-4 py-3 text-center font-medium text-slate-600">Stock Actual</th>
                  <th className="px-4 py-3 text-center font-medium text-slate-600">Mínimo</th>
                  <th className="px-4 py-3 text-center font-medium text-slate-600">Máximo</th>
                  <th className="px-4 py-3 text-center font-medium text-slate-600">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {product.stocks.map((stockInfo) => {
                  const currentEdit = edits[stockInfo.sucursal_id] || {
                    min: stockInfo.stock_min,
                    max: stockInfo.stock_max,
                  }
                  const hasChanged =
                    currentEdit.min !== stockInfo.stock_min ||
                    currentEdit.max !== stockInfo.stock_max

                  // Validación: máximo debe ser mayor que mínimo (o 0 para desactivar)
                  const isMaxInvalid =
                    currentEdit.max > 0 && currentEdit.max <= currentEdit.min

                  const canSave = hasChanged && !isMaxInvalid

                  return (
                    <tr key={stockInfo.sucursal_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {stockInfo.sucursal?.name || `Sucursal ${stockInfo.sucursal_id}`}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 font-medium ${
                            stockInfo.stock <= stockInfo.stock_min
                              ? 'bg-red-100 text-red-700'
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {stockInfo.stock}
                        </span>
                      </td>

                      {/* Mínimo */}
                      <td className="px-4 py-3 text-center">
                        <Input
                          type="number"
                          min="0"
                          className="w-20 mx-auto text-center"
                          value={currentEdit.min}
                          onChange={(e) =>
                            setEdits({
                              ...edits,
                              [stockInfo.sucursal_id]: {
                                ...currentEdit,
                                min: Math.max(0, parseInt(e.target.value) || 0),
                              },
                            })
                          }
                        />
                      </td>

                      {/* Máximo — con error inline si es inválido */}
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <Input
                            type="number"
                            min="0"
                            className={`w-20 mx-auto text-center ${
                              isMaxInvalid
                                ? 'border-red-400 focus-visible:ring-red-400'
                                : ''
                            }`}
                            value={currentEdit.max}
                            onChange={(e) =>
                              setEdits({
                                ...edits,
                                [stockInfo.sucursal_id]: {
                                  ...currentEdit,
                                  max: Math.max(0, parseInt(e.target.value) || 0),
                                },
                              })
                            }
                          />
                          {isMaxInvalid && (
                            <span className="flex items-center gap-1 text-[10px] font-medium text-red-600 whitespace-nowrap">
                              <AlertCircle className="h-3 w-3 shrink-0" />
                              Debe ser &gt; mínimo
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Acción */}
                      <td className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant={canSave ? 'default' : 'outline'}
                          disabled={!canSave || savingId === stockInfo.sucursal_id}
                          onClick={() => handleSave(stockInfo.sucursal_id)}
                          className={canSave ? 'bg-[#3B7D3B] hover:bg-[#2d6a2d]' : ''}
                          title={
                            isMaxInvalid
                              ? 'Corrige los valores antes de guardar'
                              : !hasChanged
                              ? 'Sin cambios'
                              : 'Guardar cambios'
                          }
                        >
                          {savingId === stockInfo.sucursal_id ? (
                            <Spinner className="mr-2 h-4 w-4" size="sm" />
                          ) : (
                            <Check className="mr-2 h-4 w-4" />
                          )}
                          {savingId === stockInfo.sucursal_id ? 'Guardando' : 'Guardar'}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
